import * as React from "react";
import { Link, Route } from "react-router-dom";

import { useThunkDispatch } from "@skv/state";
import { FeedbackEnum, FeedbackFragment } from ".";
import { Analytics } from "..";
import { AppRoutes } from "../../routes";

type HtmlDivAttributes = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
>;

type ErrorLayoutProps = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
>;

export const ErrorLayout: React.FC<ErrorLayoutProps> = ({
  children,
  className,
  ...rest
}) => {
  return (
    <div {...rest} className={className ?? "container py-5"}>
      {children}
    </div>
  );
};

interface DotNetReactRouterStaticContext {
  status?: number;
}

type PropertiesOf<T extends unknown> = {
  [Key in keyof T]: T[Key];
};

type WithErrorLayoutProps<T extends unknown> = {
  wrapperProps?: ErrorLayoutProps;
} & PropertiesOf<T>;

export const withErrorLayout = <Props extends unknown>(
  Component: React.ComponentType<
    Pick<WithErrorLayoutProps<Props>, Exclude<keyof Props, "wrapperProps">>
  >,
  statusCode = 200
): typeof WithErrorLayout => {
  const displayName = Component.displayName || "Component";

  const WithErrorLayout = (props: WithErrorLayoutProps<Props>): JSX.Element => {
    const { wrapperProps, ...rest } = props;

    return (
      <>
        <ErrorLayout {...wrapperProps}>
          <Component {...rest} />
        </ErrorLayout>
        <Route
          render={(props) => {
            if (props.staticContext) {
              (props.staticContext as DotNetReactRouterStaticContext).status =
                statusCode;
            }
            return null;
          }}
        />
      </>
    );
  };

  WithErrorLayout.displayName = displayName;

  return WithErrorLayout;
};

interface ErrorContactUsFragmentProps {
  title?: string;
  children?: React.ReactNode;
  contactUsTo?: string;
}

//#region Error404
export const Error404Fragment = (props: ErrorContactUsFragmentProps) => {
  const contactUsTo = props.contactUsTo || AppRoutes.ContactUs;
  const isExternal = contactUsTo.slice(0, 4) === "http";

  return (
    <FeedbackFragment
      variant={FeedbackEnum.Error}
      title={props.title ?? "This page is missing."}
    >
      {props.children ?? (
        <>
          <p>We can&apos;t find the page you were looking for.</p>
          <ul className="text-left">
            <li>Check the web address for typos.</li>
            <li>Go back to the previous page.</li>
          </ul>
          <p className="mb-0">
            If you&apos;re still stuck, let us know by{" "}
            {isExternal ? (
              <a href={contactUsTo}>contacting us</a>
            ) : (
              <Link to={contactUsTo}>contacting us</Link>
            )}
            .
          </p>
        </>
      )}
    </FeedbackFragment>
  );
};

export const Error404FragmentWithAnalytics = () => {
  return (
    <>
      <Error404Fragment />
      <Analytics
        data={{
          subSection: "support",
          subSubSection: "support:error",
          subSubSubSection: "support:error+notfound",
          pageTitle: "support:error+notfound",
          pageType: "support:error+notfound",
        }}
      />
    </>
  );
};

export const Error404 = withErrorLayout<ErrorContactUsFragmentProps>(
  Error404FragmentWithAnalytics,
  404
);
//#endregion

//#region Error500
export const Error500Fragment = ({
  contactUsTo = "/help-and-support/contact-us",
}: ErrorContactUsFragmentProps): JSX.Element => {
  const isExternal = contactUsTo.slice(0, 4) === "http";

  return (
    <FeedbackFragment
      variant={FeedbackEnum.Error}
      title="Something went wrong."
    >
      <p>We&apos;re not sure what happened, but one of these might help:</p>
      <ul className="text-left">
        <li>Go back to the previous page.</li>
        <li>Reload the page.</li>
        <li>Use a different browser or device.</li>
      </ul>
      <p className="mb-0">
        If you&apos;re still stuck, let us know by{" "}
        {isExternal ? (
          <a href={contactUsTo}>contacting us</a>
        ) : (
          <Link to={contactUsTo}>contacting us</Link>
        )}
        .
      </p>
    </FeedbackFragment>
  );
};

export const Error500 = withErrorLayout<ErrorContactUsFragmentProps>(
  Error500Fragment,
  500
);
//#endregion

//#region SharedErrors
export const ErrorInvalidOTCFragment = (): JSX.Element => {
  return (
    <FeedbackFragment
      title="Sorry we cannot accept your application at this time."
      variant={FeedbackEnum.Caution}
    >
      Please try again or reach out to our support team for further assistance.
    </FeedbackFragment>
  );
};

export const ErrorInvalidOneTimeCode = (): JSX.Element => {
  const dispatch = useThunkDispatch();

  React.useEffect(() => {
    return (): void => {
      dispatch({
        type: "OPPORTUNITY:ERROR:RESET",
      });
    };
  }, [dispatch]);

  return (
    <ErrorLayout>
      <ErrorInvalidOTCFragment />
    </ErrorLayout>
  );
};
//#endregion
